import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Users, Shield, Crown, UserCog, Eye, Mail, Search, UserPlus, ArrowLeft } from 'lucide-react';
import api from '../../lib/api';
import { fromSlug } from '../../lib/slug';
import InviteMemberModal from '../../components/InviteMemberModal';
import Button from '../../components/ui/Button';

interface TeamMember {
  id: string;
  name: string;
  email: string;
  roleInOrg: 'OWNER' | 'ADMIN' | 'MANAGER' | 'STAFF' | 'VIEWER';
  createdAt: string;
  _count?: {
    jobAssignments: number;
  };
}

const ROLE_CONFIG = {
  OWNER: {
    icon: Crown,
    color: 'text-purple-500 dark:text-purple-300',
    bg: 'bg-purple-500/15',
    border: 'border-purple-500/30',
    label: 'Owner',
    description: 'Full system access and ownership',
  },
  ADMIN: {
    icon: Shield,
    color: 'text-red-500 dark:text-red-300',
    bg: 'bg-red-500/15',
    border: 'border-red-500/30',
    label: 'Admin',
    description: 'All permissions except ownership transfer',
  },
  MANAGER: {
    icon: UserCog,
    color: 'text-accent2',
    bg: 'bg-accent2/15',
    border: 'border-accent2/30',
    label: 'Manager',
    description: 'Manage campaigns and assign staff',
  },
  STAFF: {
    icon: Users,
    color: 'text-emerald-500 dark:text-emerald-300',
    bg: 'bg-emerald-500/15',
    border: 'border-emerald-500/30',
    label: 'Staff',
    description: 'Work on assigned campaigns',
  },
  VIEWER: {
    icon: Eye,
    color: 'text-glass-text-2',
    bg: 'bg-glass-3',
    border: 'border-glass',
    label: 'Viewer',
    description: 'Read-only access',
  },
};

const ROLE_ORDER = ['OWNER', 'ADMIN', 'MANAGER', 'STAFF', 'VIEWER'];

export default function AgencyBrandTeam() {
  const { id: rawOrgId } = useParams();
  const organizationId = fromSlug(rawOrgId || '');
  const navigate = useNavigate();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>('ALL');
  const [changingRole, setChangingRole] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);

  useEffect(() => {
    fetchTeamMembers();
  }, [organizationId]);

  const fetchTeamMembers = async () => {
    if (!organizationId) return;
    try {
      setLoading(true);
      const response = await api.get(`/organizations/${organizationId}/members`);
      setMembers(response.data);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (memberId: string, newRole: string) => {
    if (!organizationId) return;
    if (!confirm(`Change this member's role to ${ROLE_CONFIG[newRole as keyof typeof ROLE_CONFIG].label}?`)) {
      return;
    }

    try {
      setChangingRole(memberId);
      await api.put(`/organizations/${organizationId}/members/${memberId}`, { roleInOrg: newRole });
      await fetchTeamMembers();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to update role');
    } finally {
      setChangingRole(null);
    }
  };

  const handleInviteSuccess = () => {
    setShowInviteModal(false);
    fetchTeamMembers();
  };

  const filteredMembers = members
    .filter((member) => {
      const matchesSearch =
        member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRole = selectedRole === 'ALL' || member.roleInOrg === selectedRole;
      return matchesSearch && matchesRole;
    })
    .sort((a, b) => {
      const roleA = ROLE_ORDER.indexOf(a.roleInOrg);
      const roleB = ROLE_ORDER.indexOf(b.roleInOrg);
      return roleA - roleB;
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent2 mx-auto"></div>
          <p className="mt-4 text-glass-text-2">Loading team members...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/agency/brands/${organizationId}`)}
            className="p-2 hover:bg-glass-3 rounded-lg text-glass-text transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-[1.75rem] font-semibold tracking-tightest text-glass-text">Brand team</h1>
            <p className="text-glass-text-2 mt-1">Manage members and roles for this brand</p>
          </div>
        </div>
        <Button onClick={() => setShowInviteModal(true)}>
          <UserPlus className="w-5 h-5 mr-2" />
          Invite Member
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {ROLE_ORDER.map((role) => {
          const config = ROLE_CONFIG[role as keyof typeof ROLE_CONFIG];
          const Icon = config.icon;
          const count = members.filter((m) => m.roleInOrg === role).length;

          return (
            <div
              key={role}
              className={`glass-card p-4 cursor-pointer transition-all hover:border-accent2/40 ${
                selectedRole === role ? 'ring-2 ring-accent2' : ''
              }`}
              onClick={() => setSelectedRole(selectedRole === role ? 'ALL' : role)}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-5 h-5 ${config.color}`} />
                <span className={`text-[0.7rem] font-medium uppercase tracking-[0.05em] ${config.color}`}>
                  {config.label}
                </span>
              </div>
              <div className="text-2xl font-semibold text-glass-text tracking-tightest">{count}</div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="glass-card p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-glass-text-3 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="glass-input w-full h-10 pl-10 pr-4 text-sm"
            />
          </div>
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            className="glass-input h-10 px-4 text-sm"
          >
            <option value="ALL">All Roles</option>
            {ROLE_ORDER.map((role) => (
              <option key={role} value={role}>
                {ROLE_CONFIG[role as keyof typeof ROLE_CONFIG].label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Team Members List */}
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-glass-1 border-b border-glass">
              <tr>
                <th className="px-6 py-3 text-left text-[0.7rem] font-medium text-glass-text-2 uppercase tracking-[0.05em]">
                  Member
                </th>
                <th className="px-6 py-3 text-left text-[0.7rem] font-medium text-glass-text-2 uppercase tracking-[0.05em]">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-[0.7rem] font-medium text-glass-text-2 uppercase tracking-[0.05em]">
                  Assigned Jobs
                </th>
                <th className="px-6 py-3 text-left text-[0.7rem] font-medium text-glass-text-2 uppercase tracking-[0.05em]">
                  Joined
                </th>
                <th className="px-6 py-3 text-left text-[0.7rem] font-medium text-glass-text-2 uppercase tracking-[0.05em]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-glass">
              {filteredMembers.map((member) => {
                const roleConfig = ROLE_CONFIG[member.roleInOrg];
                const Icon = roleConfig.icon;

                return (
                  <tr key={member.id} className="hover:bg-glass-3 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full ${roleConfig.bg} border ${roleConfig.border} flex items-center justify-center`}>
                          <span className={`text-lg font-semibold ${roleConfig.color}`}>
                            {member.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <div className="font-medium text-glass-text">{member.name}</div>
                          <div className="text-sm text-glass-text-2 flex items-center gap-2">
                            <Mail className="w-4 h-4" /> {member.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${roleConfig.color}`} />
                        <span className="text-glass-text text-sm">{roleConfig.label}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-glass-text">
                      {member._count?.jobAssignments ?? 0}
                    </td>
                    <td className="px-6 py-4 text-sm text-glass-text">
                      {new Date(member.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex items-center gap-2 flex-wrap">
                        {ROLE_ORDER.filter(r => r !== member.roleInOrg).map((role) => (
                          <button
                            key={role}
                            disabled={changingRole === member.id}
                            onClick={() => handleRoleChange(member.id, role)}
                            className="px-3 py-1 border border-glass rounded-lg hover:bg-glass-3 hover:border-accent2/40 text-glass-text text-xs transition-all"
                          >
                            Set {ROLE_CONFIG[role as keyof typeof ROLE_CONFIG].label}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {organizationId && showInviteModal && (
        <InviteMemberModal
          organizationId={organizationId}
          onClose={() => setShowInviteModal(false)}
          onSuccess={handleInviteSuccess}
        />
      )}
    </div>
  );
}